import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Map as MapIcon, Grid, Waves, Shield, Anchor, Flag, Home, ArrowRight } from 'lucide-react';
// Fix for default marker icon in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});
const fadeIn = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  },
  transition: {
    duration: 0.6
  }
};
type Community = {
  id: string;
  name: string;
  area: string;
  lat: number;
  lng: number;
  description: string;
  priceRange: string;
  amenities: string[];
  image: string;
};
const communities: Community[] = [
// Cabo San Lucas
{
  id: 'csl-1',
  name: 'Pedregal',
  area: 'Cabo San Lucas',
  lat: 22.8785,
  lng: -109.9167,
  description: 'The Beverly Hills of Cabo. Gated hillside community overlooking the marina.',
  priceRange: '$800K - $15M+',
  amenities: ['Gated', 'Beach', 'Views'],
  image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&q=80'
}, {
  id: 'csl-2',
  name: 'El Medano',
  area: 'Cabo San Lucas',
  lat: 22.8875,
  lng: -109.9075,
  description: "Beachfront living on Cabo's most popular swimmable beach.",
  priceRange: '$250K - $2M',
  amenities: ['Beach', 'Urban'],
  image: 'https://images.unsplash.com/photo-1510097467424-192d713fd8b2?auto=format&fit=crop&q=80'
}, {
  id: 'csl-3',
  name: 'Marina District',
  area: 'Cabo San Lucas',
  lat: 22.89,
  lng: -109.905,
  description: 'Walk-to-everything urban luxury. Condos and townhomes.',
  priceRange: '$200K - $1.5M',
  amenities: ['Marina', 'Urban'],
  image: 'https://images.unsplash.com/photo-1535392432937-a27c36ec07b5?auto=format&fit=crop&q=80'
}, {
  id: 'csl-4',
  name: 'Cabo Bello',
  area: 'Cabo San Lucas',
  lat: 22.905,
  lng: -109.885,
  description: 'Quiet residential area between Cabo and the Corridor.',
  priceRange: '$400K - $3M',
  amenities: ['Beach', 'Gated'],
  image: 'https://images.unsplash.com/photo-1613977257363-707ba9348227?auto=format&fit=crop&q=80'
}, {
  id: 'csl-5',
  name: 'Sunset Beach',
  area: 'Cabo San Lucas',
  lat: 22.875,
  lng: -109.925,
  description: 'Pacific-side beach community. Dramatic sunsets.',
  priceRange: '$500K - $5M',
  amenities: ['Beach', 'Views'],
  image: 'https://images.unsplash.com/photo-1506929562872-bb421503ef21?auto=format&fit=crop&q=80'
},
// The Corridor
{
  id: 'cor-1',
  name: 'Chileno Bay',
  area: 'The Corridor',
  lat: 22.935,
  lng: -109.835,
  description: 'Auberge resort community on a swimmable bay. Ultra-luxury.',
  priceRange: '$1.5M - $20M+',
  amenities: ['Beach', 'Golf', 'Gated'],
  image: 'https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?auto=format&fit=crop&q=80'
}, {
  id: 'cor-2',
  name: 'Querencia',
  area: 'The Corridor',
  lat: 22.95,
  lng: -109.82,
  description: 'Private Tom Fazio golf club. Family-oriented.',
  priceRange: '$1M - $12M',
  amenities: ['Golf', 'Gated', 'Clubhouse'],
  image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&q=80'
}, {
  id: 'cor-3',
  name: 'Palmilla',
  area: 'The Corridor',
  lat: 22.96,
  lng: -109.805,
  description: "One of Cabo's original luxury communities. One&Only resort.",
  priceRange: '$1.5M - $25M',
  amenities: ['Golf', 'Beach', 'Gated'],
  image: 'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?auto=format&fit=crop&q=80'
}, {
  id: 'cor-4',
  name: 'Cabo del Sol',
  area: 'The Corridor',
  lat: 22.92,
  lng: -109.85,
  description: 'Two championship golf courses. Ocean and desert courses.',
  priceRange: '$600K - $8M',
  amenities: ['Golf', 'Beach'],
  image: 'https://images.unsplash.com/photo-1535131749006-b7f58c99034b?auto=format&fit=crop&q=80'
}, {
  id: 'cor-5',
  name: 'Twin Dolphin',
  area: 'The Corridor',
  lat: 22.94,
  lng: -109.83,
  description: 'Montage-branded residences. Ultra-premium.',
  priceRange: '$3M - $30M+',
  amenities: ['Golf', 'Beach', 'Gated'],
  image: 'https://images.unsplash.com/photo-1613490900233-141c5560d75d?auto=format&fit=crop&q=80'
}, {
  id: 'cor-6',
  name: 'Diamante',
  area: 'The Corridor',
  lat: 22.865,
  lng: -109.935,
  description: 'Dunes course by Davis Love III. Pacific oceanfront.',
  priceRange: '$800K - $10M',
  amenities: ['Golf', 'Beach'],
  image: 'https://images.unsplash.com/photo-1512100356356-de1b84283e18?auto=format&fit=crop&q=80'
}, {
  id: 'cor-7',
  name: 'Rancho San Lucas',
  area: 'The Corridor',
  lat: 22.855,
  lng: -109.94,
  description: 'Greg Norman golf. Newest Pacific-side development.',
  priceRange: '$400K - $5M',
  amenities: ['Golf', 'Beach'],
  image: 'https://images.unsplash.com/photo-1585543805890-6051f7829f98?auto=format&fit=crop&q=80'
}, {
  id: 'cor-8',
  name: 'Club Campestre',
  area: 'The Corridor',
  lat: 22.915,
  lng: -109.86,
  description: 'Jack Nicklaus Signature golf. Desert canyon setting.',
  priceRange: '$500K - $6M',
  amenities: ['Golf', 'Gated'],
  image: 'https://images.unsplash.com/photo-1514933651103-005eec06c04b?auto=format&fit=crop&q=80'
},
// San José del Cabo
{
  id: 'sjc-1',
  name: 'Puerto Los Cabos',
  area: 'San José del Cabo',
  lat: 23.04,
  lng: -109.7,
  description: 'Marina community with two golf courses.',
  priceRange: '$500K - $8M',
  amenities: ['Golf', 'Marina', 'Beach'],
  image: 'https://images.unsplash.com/photo-1544984243-ec57ea16fe25?auto=format&fit=crop&q=80'
}, {
  id: 'sjc-2',
  name: 'Fundadores',
  area: 'San José del Cabo',
  lat: 23.055,
  lng: -109.69,
  description: 'Beachfront enclave near downtown San José.',
  priceRange: '$600K - $4M',
  amenities: ['Beach', 'Gated'],
  image: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&q=80'
}, {
  id: 'sjc-3',
  name: 'La Playita',
  area: 'San José del Cabo',
  lat: 23.05,
  lng: -109.685,
  description: 'Charming fishing village turned boutique community.',
  priceRange: '$300K - $2M',
  amenities: ['Marina', 'Urban'],
  image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&q=80'
},
// East Cape
{
  id: 'ec-1',
  name: 'Costa Palmas',
  area: 'East Cape',
  lat: 23.35,
  lng: -109.55,
  description: 'Four Seasons and Amanvari. Remote ultra-luxury.',
  priceRange: '$2M - $30M+',
  amenities: ['Golf', 'Beach', 'Marina'],
  image: 'https://images.unsplash.com/photo-1582719478250-c89400bb1536?auto=format&fit=crop&q=80'
}, {
  id: 'ec-2',
  name: 'Vinorama',
  area: 'East Cape',
  lat: 23.2,
  lng: -109.6,
  description: 'Emerging area with large lots and ocean views.',
  priceRange: '$150K - $2M',
  amenities: ['Beach', 'Views'],
  image: 'https://images.unsplash.com/photo-1519046904884-53103b34b206?auto=format&fit=crop&q=80'
}, {
  id: 'ec-3',
  name: 'East Cape General',
  area: 'East Cape',
  lat: 23.15,
  lng: -109.62,
  description: 'Off-grid, surf culture, large parcels.',
  priceRange: '$100K - $1M',
  amenities: ['Beach'],
  image: 'https://images.unsplash.com/photo-1505881502353-a1986add3762?auto=format&fit=crop&q=80'
},
// Pacific Side
{
  id: 'pac-1',
  name: 'Cerritos',
  area: 'Pacific Side',
  lat: 23.32,
  lng: -109.98,
  description: 'Surf community near Todos Santos. Bohemian luxury.',
  priceRange: '$200K - $2M',
  amenities: ['Beach', 'Surf'],
  image: 'https://images.unsplash.com/photo-1502680390469-be75c86b636f?auto=format&fit=crop&q=80'
}, {
  id: 'pac-2',
  name: 'Todos Santos',
  area: 'Pacific Side',
  lat: 23.45,
  lng: -110.22,
  description: 'Artist colony, boutique hotels, magic town.',
  priceRange: '$250K - $3M',
  amenities: ['Urban', 'Culture'],
  image: 'https://images.unsplash.com/photo-1518548419970-58e3b4079ab2?auto=format&fit=crop&q=80'
}, {
  id: 'pac-3',
  name: 'El Pescadero',
  area: 'Pacific Side',
  lat: 23.36,
  lng: -110.05,
  description: 'Farm-to-table lifestyle, organic farms, surf.',
  priceRange: '$150K - $1.5M',
  amenities: ['Farming', 'Beach'],
  image: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&q=80'
}];
const areas = ['All', 'Cabo San Lucas', 'The Corridor', 'San José del Cabo', 'East Cape', 'Pacific Side'];
const AmenityIcon = ({
  type
}: {
  type: string;
}) => {
  switch (type) {
    case 'Golf':
      return <Flag size={14} className="mr-1" data-id="element-453" data-mp="zqk1sd9tnwjz" />;
    case 'Beach':
    case 'Surf':
      return <Waves size={14} className="mr-1" data-id="element-454" data-mp="c3k87jws5mrk" />;
    case 'Marina':
      return <Anchor size={14} className="mr-1" data-id="element-455" data-mp="74qy81gpcdw5" />;
    case 'Gated':
      return <Shield size={14} className="mr-1" data-id="element-456" data-mp="lsvi38rhqvgi" />;
    default:
      return <Home size={14} className="mr-1" data-id="element-457" data-mp="luf0p60mdilg" />;
  }
};
export function Communities() {
  const [viewMode, setViewMode] = useState<'grid' | 'map'>('grid');
  const [activeArea, setActiveArea] = useState('All');
  const filteredCommunities = activeArea === 'All' ? communities : communities.filter(c => c.area === activeArea);
  return <div className="w-full bg-sand min-h-screen pt-24" data-id="element-458" data-mp="3ds98cezzm7cf">
      {/* Header */}
      <section className="py-16 px-4 text-center max-w-4xl mx-auto" data-id="element-459" data-mp="3dq6ka0ieaoac">
        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-4xl md:text-6xl font-serif text-navy mb-6" data-id="element-460" data-mp="39rfkflmjosjj">
          Cabo Communities
        </motion.h1>
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.2
      }} className="text-lg md:text-xl font-light text-navy/70 leading-relaxed mb-10" data-id="element-461" data-mp="2uv810niynv5b">
          From the vibrant Marina to the serene East Cape, explore the distinct
          neighborhoods and luxury enclaves of Baja California Sur.
        </motion.p>

        {/* View Toggles */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="flex justify-center space-x-4 mb-12" data-id="element-462" data-mp="275dpc9adocmn">
          <button onClick={() => setViewMode('grid')} className={`flex items-center px-6 py-3 uppercase tracking-wider text-sm font-medium transition-colors ${viewMode === 'grid' ? 'bg-navy text-white' : 'bg-white text-navy border border-navy/20 hover:border-navy'}`} data-id="element-463" data-mp="26zmsxd4g0o17">
            <Grid size={18} className="mr-2" data-id="element-464" data-mp="1j98zpja4gj51" /> Grid View
          </button>
          <button onClick={() => setViewMode('map')} className={`flex items-center px-6 py-3 uppercase tracking-wider text-sm font-medium transition-colors ${viewMode === 'map' ? 'bg-navy text-white' : 'bg-white text-navy border border-navy/20 hover:border-navy'}`} data-id="element-465" data-mp="1o7k77ncy2hk3">
            <MapIcon size={18} className="mr-2" data-id="element-466" data-mp="10h7sntn71mkj" /> Map View
          </button>
        </motion.div>
      </section>

      {/* Content Area */}
      <section className="pb-24" data-id="element-467" data-mp="sfgxtgprua7g">
        {viewMode === 'grid' ? <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-468" data-mp="nhp6oowyas4b">
            {/* Filters */}
            <motion.div {...fadeIn} className="flex flex-wrap justify-center gap-3 mb-12" data-id="element-469" data-mp="126bnyw651ty6">
              {areas.map(area => <button key={area} onClick={() => setActiveArea(area)} className={`px-5 py-2 text-xs uppercase tracking-widest transition-colors border rounded-full ${activeArea === area ? 'bg-gold border-gold text-white font-semibold' : 'bg-white border-navy/10 text-navy/70 hover:border-gold hover:text-navy'}`} data-id="element-470" data-mp="9i5yxx81xisj">
                  {area}
                </button>)}
            </motion.div>

            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-471" data-mp="1pxh296wfke6d">
              {filteredCommunities.map((community, i) => <motion.div key={community.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.05
          }} className="bg-white group overflow-hidden shadow-sm hover:shadow-md transition-all border border-navy/5" data-id="element-472" data-mp="1xf5er3v5ghue" data-mpi={i}>
                  <div className="h-64 overflow-hidden relative" data-id="element-473" data-mp="2l85ecu0mkw77">
                    <img src={community.image} alt={community.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-474" data-mp="27dv0j7q62mfx" />
                    <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 text-xs font-bold text-navy uppercase tracking-wider" data-id="element-475" data-mp="33witfoxrbef3">
                      {community.area}
                    </div>
                  </div>
                  <div className="p-6" data-id="element-476" data-mp="1b82xhk0ik13n">
                    <div className="flex justify-between items-start mb-2" data-id="element-477" data-mp="1bb78p1cebx3t">
                      <h3 className="text-2xl font-serif text-navy" data-id="element-478" data-mp="16ajjml7q9qc8">
                        {community.name}
                      </h3>
                    </div>
                    <p className="text-navy/70 font-light text-sm mb-6 h-10 line-clamp-2" data-id="element-479" data-mp="1yyymyahhyk7q">
                      {community.description}
                    </p>

                    <div className="border-t border-navy/10 pt-4 flex justify-between items-center" data-id="element-480" data-mp="1u1ygvzkndhgm">
                      <div className="flex flex-wrap gap-2" data-id="element-481" data-mp="1p6j5rfe0xznw">
                        {community.amenities.map(amenity => <span key={amenity} className="flex items-center text-xs font-medium text-navy/60 bg-sand px-2 py-1 rounded-sm" data-id="element-482" data-mp="2hpq5o2ndjmys">
                            <AmenityIcon type={amenity} data-id="element-483" data-mp="3vm9ivkvuoewf" />
                            {amenity}
                          </span>)}
                      </div>
                      <span className="text-gold font-semibold text-sm" data-id="element-484" data-mp="8g8vlnkvj8ui">
                        {community.priceRange}
                      </span>
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </div> : <div className="w-full h-[70vh] min-h-[500px] relative z-0" data-id="element-485" data-mp="ndj20iintojd">
            <MapContainer center={[23.05, -109.8]} zoom={10} scrollWheelZoom={false} className="w-full h-full" data-id="element-486" data-mp="nbg3tb7csp3k">
              <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>' url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" data-id="element-487" data-mp="w5b9qlgaujk9" />
              {communities.map(community => <Marker key={community.id} position={[community.lat, community.lng]} data-id="element-488" data-mp="2x8u81ft9y3qq">
                  <Popup className="custom-popup" data-id="element-489" data-mp="3byt8xqc7rt9w">
                    <div className="p-1 min-w-[200px]" data-id="element-490" data-mp="376028wx6qav6">
                      <img src={community.image} alt={community.name} className="w-full h-24 object-cover rounded-sm mb-3" data-id="element-491" data-mp="374f6j3z4wlvq" />
                      <span className="text-[10px] uppercase tracking-widest text-gold font-bold block mb-1" data-id="element-492" data-mp="3uw2qt31w4yp1">
                        {community.area}
                      </span>
                      <h4 className="font-serif text-lg text-navy m-0 mb-2" data-id="element-493" data-mp="3puwwx1whv5dx">
                        {community.name}
                      </h4>
                      <p className="text-xs text-navy/70 mb-2" data-id="element-494" data-mp="1vji3ob49yntf">
                        {community.description}
                      </p>
                      <div className="font-semibold text-navy text-sm" data-id="element-495" data-mp="b1g04i6aeh6m">
                        {community.priceRange}
                      </div>
                    </div>
                  </Popup>
                </Marker>)}
            </MapContainer>
          </div>}
      </section>
    </div>;
}